import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Upload, Trash2, Star, Play, Image as ImageIcon } from 'lucide-react';

const BLUE = '#0088CC';
const BLUE_LIGHT = '#00CCFF';

const authAxios = () => {
  const token = localStorage.getItem('bar_owner_token');
  return axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
};

export default function OwnerMediaGallery() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const res = await authAxios().get('/owner/media');
      setItems(res.data || []);
    } catch (err) { /* silent */ }
  };

  useEffect(() => { load(); }, []);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    for (const file of Array.from(files)) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 50MB limit`);
        continue;
      }
      const fd = new FormData();
      fd.append('file', file);
      try {
        await authAxios().post('/owner/media/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success(`Uploaded ${file.name}`);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
    load();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    uploadFiles(e.dataTransfer.files);
  };

  const handleDelete = async (filename) => {
    try {
      await authAxios().delete(`/owner/media/${filename}`);
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const handleSetCover = async (filename) => {
    try {
      await authAxios().post(`/owner/media/${filename}/set-cover`);
      toast.success('Set as bar cover');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const BACKEND_ORIGIN = API.replace(/\/api$/, '');

  return (
    <Card
      data-testid="owner-media-gallery"
      className="p-5 border"
      style={{
        background: 'rgba(0, 20, 40, 0.6)',
        borderColor: `${BLUE}55`,
        boxShadow: `0 0 15px ${BLUE}22`
      }}
    >
      <h2
        className="text-xl font-bold mb-1"
        style={{ color: BLUE_LIGHT, textShadow: `0 0 10px ${BLUE}99` }}
      >
        📸 Media Gallery — Upload Photos &amp; Videos
      </h2>
      <p className="text-xs text-slate-400 mb-4">Max 50MB per file · JPG, PNG, WEBP, MP4, MOV</p>

      {/* Drag & drop zone */}
      <div
        data-testid="media-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className="cursor-pointer rounded-lg p-8 text-center transition-all mb-5"
        style={{
          border: `2px dashed ${dragActive ? BLUE_LIGHT : BLUE}88`,
          background: dragActive ? `${BLUE}22` : 'rgba(0, 15, 30, 0.4)',
        }}
      >
        <Upload className="w-10 h-10 mx-auto mb-2" style={{ color: BLUE_LIGHT }} />
        <p className="font-semibold" style={{ color: BLUE_LIGHT }}>
          {uploading ? 'Uploading...' : 'Drop files here or click to browse'}
        </p>
        <p className="text-xs text-slate-400 mt-1">Max 50MB per file</p>
        <input
          ref={inputRef}
          data-testid="media-file-input"
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime"
          onChange={(e) => uploadFiles(e.target.files)}
          className="hidden"
        />
      </div>

      {/* Grid */}
      {items.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-4">No media uploaded yet.</p>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {items.map((m) => {
            const src = m.url.startsWith('http') ? m.url : `${BACKEND_ORIGIN}${m.url}`;
            return (
              <div
                key={m.id || m.filename}
                data-testid={`media-thumb-${m.filename}`}
                className="relative rounded-lg overflow-hidden group"
                style={{
                  border: `1px solid ${m.is_cover ? BLUE_LIGHT : BLUE + '55'}`,
                  boxShadow: m.is_cover ? `0 0 15px ${BLUE_LIGHT}88` : 'none'
                }}
              >
                {m.media_type === 'video' ? (
                  <div className="relative bg-black aspect-square flex items-center justify-center">
                    <video src={src} className="w-full h-full object-cover" muted preload="metadata" />
                    <Play className="absolute w-10 h-10 text-white/80" />
                  </div>
                ) : (
                  <img src={src} alt={m.original_name} className="w-full aspect-square object-cover" />
                )}

                {m.is_cover && (
                  <div
                    className="absolute top-1 left-1 text-[9px] font-bold px-2 py-0.5 rounded-full"
                    style={{ background: BLUE_LIGHT, color: '#001525' }}
                  >
                    ★ COVER
                  </div>
                )}

                {/* Hover controls */}
                <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
                  {m.media_type === 'image' && !m.is_cover && (
                    <Button
                      data-testid={`set-cover-${m.filename}`}
                      onClick={() => handleSetCover(m.filename)}
                      size="sm"
                      className="text-xs"
                      style={{ background: BLUE, color: '#fff' }}
                    >
                      <Star className="w-3 h-3 mr-1" /> Set as Cover
                    </Button>
                  )}
                  <Button
                    data-testid={`delete-${m.filename}`}
                    onClick={() => handleDelete(m.filename)}
                    size="sm"
                    variant="destructive"
                    className="text-xs bg-red-600 hover:bg-red-500"
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
